import { useRef } from 'react';
import { cn } from '@/lib/utils';

export interface StatusStepperStep<T extends string> {
  value: T;
  label: string;
  className: string;
}

interface StatusStepperProps<T extends string> {
  steps: readonly StatusStepperStep<T>[];
  value: T;
  onChange: (next: T) => void;
  ariaLabel?: string;
  /** Return a non-empty string to trigger window.confirm; return null to skip. */
  confirmRequiredOn?: (from: T, to: T) => string | null;
  disabled?: boolean;
}

export function StatusStepper<T extends string>({
  steps,
  value,
  onChange,
  ariaLabel,
  confirmRequiredOn,
  disabled,
}: StatusStepperProps<T>) {
  const groupRef = useRef<HTMLDivElement>(null);

  const tryChange = (next: T) => {
    if (next === value) return;
    if (confirmRequiredOn) {
      const message = confirmRequiredOn(value, next);
      if (message && !window.confirm(message)) return;
    }
    onChange(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const idx = steps.findIndex((s) => s.value === value);
    if (e.key === 'ArrowRight' && idx < steps.length - 1) {
      e.preventDefault();
      tryChange(steps[idx + 1]!.value);
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      tryChange(steps[idx - 1]!.value);
    }
  };

  return (
    // Four steps are wider than a phone screen, and the segments have to stay on
    // one line to read as a track — so the strip scrolls on its own rather than
    // pushing the page sideways.
    <div className="-mx-1 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        className="inline-flex w-max rounded-md border overflow-hidden"
      >
        {steps.map((step) => {
          const checked = step.value === value;
          return (
            <button
              key={step.value}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => tryChange(step.value)}
              className={cn(
                'shrink-0 whitespace-nowrap border-r px-3 py-2 text-sm font-medium last:border-r-0 transition-colors sm:py-1.5',
                checked ? step.className : 'bg-white text-gray-600 hover:bg-gray-50',
                disabled && 'opacity-50 cursor-not-allowed',
              )}
            >
              {step.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
